import { useMemo, useState } from 'react';
import {
  ListBullets,
  Shield,
  Clock,
  Check,
  PencilSimple,
  Trash,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { ActionMenu, GenericFilters, GenericTables } from '../../../shared/components/ui';
import TechniciansAvatarGroup from './TechniciansAvatarGroup';

function getStatus(record, today) {
  if (!record.startDate) return 'scheduled';
  if (record.startDate > today) return 'scheduled';
  if (record.endDate && record.endDate < today) return 'completed';
  return 'active';
}

function formatPeriod(record) {
  const format = (dateObj) => {
    if (!dateObj) return '-';
    const d = String(dateObj.getDate()).padStart(2, '0');
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    return `${d}/${m}`;
  };

  const start = format(record.startDate);
  const end = format(record.endDate);
  return `${start} - ${end}`;
}

function getIsoWeek(dateObj) {
  if (!dateObj) return '-';
  const date = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

const statusMap = {
  active: { label: 'En Curso', icon: Shield, className: 'bg-success/12 text-success border border-success' },
  completed: { label: 'Completada', icon: Check, className: 'bg-muted-deep/12 text-muted-deep border border-muted-deep' },
  scheduled: { label: 'Programada', icon: Clock, className: 'bg-amber/12 text-amber border border-amber' },
};

export function GuardsTable({
  records,
  teamsById,
  techniciansByTeamId = {},
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  filters = { status: '' },
  onFilterChange = () => {},
  onRestoreGuard,
  onDeleteGuard,
  isLoading = false,
  isMutating,
  canManage = false,
}) {

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const rows = useMemo(() => {
    return records.map((record) => {
      const weekEndAt = record.startDate
        ? new Date(record.startDate.getFullYear(), record.startDate.getMonth(), record.startDate.getDate() + 6)
        : null;

      return {
        ...record,
        status: getStatus(record, today),
        week: getIsoWeek(record.startDate),
        period: formatPeriod(record),
        weekEndAt,
        weekEndYmd: weekEndAt
          ? `${weekEndAt.getFullYear()}-${String(weekEndAt.getMonth() + 1).padStart(2, '0')}-${String(weekEndAt.getDate()).padStart(2, '0')}`
          : null,
        teamName: teamsById[record.teamId]?.name || `Equipo ${record.teamId}`,
        technicians: techniciansByTeamId[record.teamId] || [],
        teamColor: 'var(--info)',
      };
    });
  }, [records, teamsById, techniciansByTeamId, today]);

  const data = useMemo(() => {
    if (filters.status === 'all') return rows;
    return rows.filter((r) => r.status === filters.status);
  }, [rows, filters.status]);

  const filterColumns = [
    {
      type: 'select',
      key: 'status',
      label: 'Estado',
      options: [
        { id: 'all', name: 'Todos' },
        { id: 'active', name: 'En Curso' },
        { id: 'scheduled', name: 'Programadas' },
        { id: 'completed', name: 'Completadas' },
      ],
      optionValue: 'id',
      optionLabel: 'name',
      defaultValue: 'all',
      colSpan: 1,
    },
  ];

  const tableColumns = [
    {
      key: 'week',
      label: 'Semana',
      align: 'center',
      render: (row) => <span className="font-bold text-content">Semana {row.week}</span>,
    },
    {
      key: 'period',
      label: 'Periodo',
      align: 'center',
      render: (row) => <span className="text-content">{row.period}</span>,
    },
    {
      key: 'teamName',
      label: 'Equipo',
      align: 'center',
      render: (row) => (
        <div className="inline-flex items-center gap-2 text-content font-semibold">
          {row.teamName}
        </div>
      ),
    },
    {
      key: 'technicians',
      label: 'Tecnicos',
      align: 'center',
      render: (row) => (
        <TechniciansAvatarGroup technicians={row.technicians} maxVisible={4} showCount />
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      align: 'center',
      render: (row) => {
        const status = statusMap[row.status];
        const StatusIcon = status.icon;
        return (
          <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${status.className}`}>
            <StatusIcon size={16} />
            {status.label}
          </span>
        );
      },
    },
    ...(canManage ? [{
      key: 'actions',
      label: 'Acciones',
      align: 'center',
      isAction: true,
      render: (row) => {
        const canRestore = row.isInterrupted
          && row.weekEndAt
          && row.weekEndAt >= today
          && row.endAt !== row.weekEndYmd;

        return (
          <ActionMenu
            options={[
              ...(canRestore ? [{
                label: 'Restaurar guardia',
                icon: <ArrowCounterClockwise size={18} />,
                disabled: isMutating,
                onClick: () => onRestoreGuard({
                  currentRecord: row,
                  endAt: row.weekEndYmd,
                }),
              }] : []),
              ...(row.status === 'scheduled' ? [{
                label: 'Editar',
                icon: <PencilSimple size={18} />,
                disabled: true,
                onClick: () => {},
              }] : []),
              {
                label: 'Quitar guardia',
                icon: <Trash size={18} />,
                disabled: isMutating,
                onClick: () => onDeleteGuard({ teamId: row.teamId, startAt: row.startAt }),
              },
            ]}
          />
        );
      },
    }] : []),
  ];

  const skeletonRows = Array.from({ length: pageSize }, (_, index) => index);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <ListBullets size={20} className="text-content" />
        <h2 className="text-xl font-bold text-content">Historial de Guardias</h2>
      </div>

      <GenericFilters
        columns={filterColumns}
        filters={filters}
        onFilterChange={onFilterChange}
        onClearFilters={() => onFilterChange({ status: 'all' })}
        defaultOpen={false}
      />

      {isLoading ? (
        <div className="rounded-xl md:bg-surface md:shadow-lg md:border md:border-line md:overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-table-head shadow-sm">
                <tr>
                  {tableColumns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-6 py-4 text-${column.align || 'center'} text-sm font-bold text-content border-b-2 border-line`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-line">
                {skeletonRows.map((rowIndex) => (
                  <tr key={rowIndex} className="odd:!bg-surface even:!bg-surface-2">
                    {tableColumns.map((column) => (
                      <td key={`${rowIndex}-${column.key}`} className="px-6 py-4">
                        <div className={`flex items-center justify-${column.align || 'center'}`}>
                          <div className="h-5 w-full max-w-[140px] animate-pulse rounded bg-surface-2" />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {skeletonRows.slice(0, Math.min(pageSize, 4)).map((rowIndex) => (
              <div
                key={rowIndex}
                className="rounded-xl shadow-md p-4 space-y-4 bg-surface border border-line animate-pulse"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="h-5 w-40 rounded bg-surface-2" />
                  <div className="h-8 w-24 rounded-full bg-surface-2" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-12 rounded bg-surface-2" />
                  <div className="h-12 rounded bg-surface-2" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-12 rounded bg-surface-2" />
                  <div className="h-12 rounded bg-surface-2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <GenericTables
          columns={tableColumns}
          data={data}
          emptyMessage="No hay registros para el filtro seleccionado."
          zebraStripe
          showFooter
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            totalItems,
            pageSizeOptions: [5, 10, 20, 50],
            onPageChange: onPageChange || (() => {}),
            onPageSizeChange: onPageSizeChange || (() => {}),
          }}
        />
      )}
    </div>
  );
}
